import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { FieldLabel } from "@/pages/admin/FieldLabel";

export default function GalleryManager({ images, onCreated, onDeleted }) {
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const fileRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error("Only JPEG, PNG, WebP or GIF images are allowed");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image must be under 12 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (caption.trim()) fd.append("caption", caption.trim());
    try {
      const { data } = await api.post("/admin/gallery", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onCreated(data);
      toast.success("Photo uploaded — live on the site");
      setCaption("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (img) => {
    if (!window.confirm("Remove this photo from the website?")) return;
    try {
      await api.delete(`/admin/gallery/${img.id}`);
      onDeleted(img.id);
      toast.success("Photo removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const apiBase = (api.defaults?.baseURL || "").replace(/\/api\/?$/, "");
  const resolveUrl = (u) => (u && u.startsWith("/") ? `${apiBase}${u}` : u);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 border-l-2 border-brand-red pl-4 mb-2">
        <ImagePlus className="h-5 w-5 text-brand-red mt-0.5" />
        <div>
          <h3 className="font-display uppercase text-xl text-white tracking-tight">Workshop gallery</h3>
          <p className="text-sm text-zinc-400 max-w-xl mt-1">
            Upload photos of your workshop, tools and team. They appear instantly in the public gallery section on the About page.
          </p>
        </div>
      </div>

      <form
        data-testid="gallery-upload-form"
        onSubmit={(e) => {
          e.preventDefault();
          upload(fileRef.current?.files?.[0]);
        }}
        className="border border-zinc-900 bg-zinc-950 p-6 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldLabel label="Photo (JPEG/PNG/WebP, ≤ 12 MB)">
            <input
              ref={fileRef}
              data-testid="gallery-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="ta-svc-input file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-brand-red file:text-white file:font-mono file:text-[10px] file:uppercase file:tracking-widest"
            />
          </FieldLabel>
          <FieldLabel label="Caption (optional)">
            <input
              data-testid="gallery-caption-input"
              className="ta-svc-input"
              placeholder="e.g. Brake bay 02 — new hoist"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </FieldLabel>
        </div>
        <button
          data-testid="gallery-upload-submit"
          disabled={uploading}
          className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50"
        >
          <ImagePlus className="h-3.5 w-3.5" /> {uploading ? "Uploading…" : "Upload photo"}
        </button>
      </form>

      {!images.length ? (
        <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-10 px-1">
          No photos yet — upload your first one above.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
          {images.map((img) => (
            <figure key={img.id} data-testid={`gallery-item-${img.id}`} className="bg-zinc-950 relative group">
              <img
                src={resolveUrl(img.url)}
                alt={img.caption || "Workshop photo"}
                className="w-full h-44 object-cover"
                loading="lazy"
              />
              <button
                data-testid={`gallery-del-${img.id}`}
                onClick={() => remove(img)}
                title="Remove from website"
                className="absolute top-2 right-2 inline-flex items-center justify-center h-8 w-8 bg-black/70 text-zinc-300 hover:text-red-400 hover:bg-black/90 border border-zinc-800 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="h-4 w-4" />
              </button>
              {img.caption && (
                <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-3 py-2 text-[11px] text-zinc-200 leading-snug">
                  {img.caption}
                </figcaption>
              )}
              {img.is_uploaded && (
                <span className="absolute top-2 left-2 px-2 py-0.5 bg-brand-red/90 text-white font-mono text-[9px] uppercase tracking-widest">
                  Yours
                </span>
              )}
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}


